import { getMinorSafeDisplayName, isMinorProfile } from "./minorSafety";

export const normalizeCollection = (payload) => {
  const candidate =
    payload?.content ||
    payload?.items ||
    payload?.data ||
    payload?.results ||
    payload?.list ||
    payload?.lists ||
    payload?.keywords ||
    payload?.users ||
    payload?.connections ||
    payload?.followers ||
    payload?.following ||
    payload ||
    [];

  return Array.isArray(candidate) ? candidate : [];
};

export const normalizePersonRecord = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const nestedCandidateKeys = [
    "user",
    "profile",
    "partner",
    "otherUser",
    "fromUser",
    "toUser",
    "data",
    "item",
  ];

  const nestedCandidate = nestedCandidateKeys
    .map((key) => raw?.[key])
    .find((value) => value && typeof value === "object");

  const source = nestedCandidate || raw;

  const uuid =
    source.uuid ||
    source.id ||
    source.userId ||
    source.userUuid ||
    raw.uuid ||
    raw.id ||
    raw.userId ||
    raw.userUuid ||
    null;
  const username =
    source.username ||
    source.userName ||
    source.preferredUsername ||
    source.handle ||
    source.email ||
    raw.username ||
    raw.userName ||
    raw.preferredUsername ||
    raw.handle ||
    raw.email ||
    "";
  const mergedProfile = { ...raw, ...source };
  const displayName = getMinorSafeDisplayName(mergedProfile);
  const accountType =
    source.accountType ||
    source.account_type ||
    source.typeString ||
    source.type ||
    source.profile_role ||
    raw.accountType ||
    raw.account_type ||
    raw.typeString ||
    raw.type ||
    raw.profile_role ||
    null;

  return {
    ...raw,
    ...source,
    accountType,
    role: source.role || raw.role || accountType,
    type: source.type || raw.type || accountType,
    uuid,
    username,
    displayName:
      displayName ||
      username ||
      source.displayName ||
      raw.displayName ||
      source.name ||
      raw.name ||
      "User",
    avatarURL:
      source.avatarURL ||
      source.avatarUrl ||
      source.avatar ||
      source.photoUrl ||
      source.profileImageUrl ||
      source.profileImage ||
      source.imageUrl ||
      source.image ||
      source.picture ||
      raw.avatarURL ||
      raw.avatarUrl ||
      raw.avatar ||
      raw.photoUrl ||
      raw.profileImageUrl ||
      raw.profileImage ||
      raw.imageUrl ||
      raw.image ||
      raw.picture ||
      null,
    bannerURL:
      source.bannerURL ||
      source.bannerUrl ||
      source.banner ||
      source.coverImageUrl ||
      source.coverImage ||
      source.headerImageUrl ||
      source.headerImage ||
      source.backgroundImageUrl ||
      source.backgroundImage ||
      raw.bannerURL ||
      raw.bannerUrl ||
      raw.banner ||
      raw.coverImageUrl ||
      raw.coverImage ||
      raw.headerImageUrl ||
      raw.headerImage ||
      raw.backgroundImageUrl ||
      raw.backgroundImage ||
      null,
    studiesAt:
      source.studiesAt ||
      source.placeOfStudy ||
      source.placeOfTeaching ||
      source.studies ||
      source.organizationName ||
      raw.studiesAt ||
      raw.placeOfStudy ||
      raw.placeOfTeaching ||
      raw.studies ||
      raw.organizationName ||
      null,
    highestQualification:
      source.highestQualification ||
      source.degree ||
      source.educationLevel ||
      raw.highestQualification ||
      raw.degree ||
      raw.educationLevel ||
      null,
    department:
      source.department ||
      source.organisationDepartment ||
      raw.department ||
      raw.organisationDepartment ||
      null,
    aboutMe:
      source.aboutMe ||
      source.bio ||
      source.description ||
      raw.aboutMe ||
      raw.bio ||
      raw.description ||
      null,
  };
};

const normalizeText = (value) => {
  if (value == null) return "";
  return String(value).trim();
};

const toSearchableValue = (value) => normalizeText(value).toLowerCase();

export const getSubjectLabel = (subject) => {
  if (!subject) return "";
  if (typeof subject === "string") return normalizeText(subject);
  return normalizeText(
    subject?.name ||
      subject?.title ||
      subject?.label ||
      subject?.subjectName ||
      subject?.subject,
  );
};

export const getTopicLabel = (value) => {
  if (!value) return "";

  if (Array.isArray(value)) {
    return value
      .map((entry) => getTopicLabel(entry))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "string") {
    return normalizeText(value);
  }

  return normalizeText(value?.name || value?.title || value?.label);
};

export const getUserSearchFields = (user) => {
  const normalizedUser = normalizePersonRecord(user);
  if (!normalizedUser) return [];

  return [
    normalizedUser.displayName,
    isMinorProfile(normalizedUser) ? "" : normalizedUser.name,
    normalizedUser.firstName,
    isMinorProfile(normalizedUser) ? "" : normalizedUser.lastName,
    normalizedUser.username,
    normalizedUser.userName,
    normalizedUser.preferredUsername,
    normalizedUser.handle,
    normalizedUser.highestQualification,
    normalizedUser.studiesAt,
    normalizedUser.department,
  ].filter(Boolean);
};

export const getListSearchFields = (listItem) => {
  const subjectLabel = getSubjectLabel(listItem?.subject || listItem?.subjectName);
  const topicLabel =
    getTopicLabel(listItem?.topic) ||
    getTopicLabel(listItem?.topics) ||
    getTopicLabel(listItem?.category);
  const keywordTerms = Array.isArray(listItem?.keywords)
    ? listItem.keywords
        .map((keyword) =>
          normalizeText(
            keyword?.term || keyword?.name || keyword?.keyword || keyword?.title,
          ),
        )
        .filter(Boolean)
        .join(" ")
    : "";

  return [
    listItem?.name,
    listItem?.title,
    listItem?.listName,
    listItem?.description,
    subjectLabel,
    topicLabel,
    keywordTerms,
  ].filter(Boolean);
};

export const getKeywordLabel = (keyword) =>
  normalizeText(
    keyword?.term ||
      keyword?.name ||
      keyword?.keyword ||
      keyword?.title ||
      keyword?.question ||
      "Keyword",
  );

export const getKeywordSearchFields = (keyword) => {
  const subjectLabel = getSubjectLabel(keyword?.subject || keyword?.subjectName);
  const topicLabel =
    getTopicLabel(keyword?.topic) ||
    getTopicLabel(keyword?.topics) ||
    getTopicLabel(keyword?.category);

  return [
    getKeywordLabel(keyword),
    keyword?.question,
    keyword?.definition,
    keyword?.context,
    keyword?.listName,
    keyword?.listTitle,
    subjectLabel,
    topicLabel,
  ].filter(Boolean);
};

export const matchesSearchQuery = (fields, query) => {
  const normalizedQuery = toSearchableValue(query);
  if (!normalizedQuery) return true;

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  const haystack = fields
    .map((field) => toSearchableValue(field))
    .filter(Boolean)
    .join(" ");

  return tokens.every((token) => haystack.includes(token));
};

export const filterBySearchQuery = (items, query, buildFields) =>
  (Array.isArray(items) ? items : []).filter((item) =>
    matchesSearchQuery(buildFields(item), query),
  );
